import React, { useCallback, useEffect, useState } from 'react';
import { Typography, Button, Spin, Alert, Empty, Modal, Table, Descriptions, message } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import { professorPagamentosService } from '../../services/api';
import { buildApiErrorMessage } from '../../utils/apiError';
import { useAuth } from '../../contexts/AuthContext';
import PagamentoProfessorCard from './PagamentoProfessorCard';
import '../../styles/PortalProfessor.css';

const { Title, Text } = Typography;

const MENSAGEM_SEM_VINCULO =
  'Seu usuário ainda não está vinculado a um professor/equipe. Procure a administração.';

const STATUS_LABEL = { RASCUNHO: 'Rascunho', FECHADA: 'Fechada', PAGA: 'Paga' };

const formatarValor = (valor) => `R$ ${Number(valor || 0).toFixed(2)}`;
const formatarCompetencia = (mesReferencia) => dayjs(`${mesReferencia}-01`).format('MMMM/YYYY');

// Portal do Professor -- Pagamentos (Fase 14D). Somente leitura: sem
// Gerar/Fechar/Dar baixa/Reverter/Editar/Excluir/Ajuste retroativo --
// nenhuma dessas ações existe nesta tela, mesmo padrão já usado em Aulas
// Dadas/Turmas do Portal. O recibo é montado inteiramente a partir dos
// dados de GET /api/professor/pagamentos/{id} -- esta tela NUNCA chama
// /api/folha-pagamento/* (endpoints administrativos).
const PortalProfessorPagamentos = () => {
  const { user } = useAuth();
  const [pagamentos, setPagamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null); // { status, message } | null

  const [modalAberto, setModalAberto] = useState(false);
  const [carregandoDetalhe, setCarregandoDetalhe] = useState(false);
  const [detalhe, setDetalhe] = useState(null);
  const [erroDetalhe, setErroDetalhe] = useState(null);

  const carregarPagamentos = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      const response = await professorPagamentosService.listar();
      setPagamentos(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        setErro({ status, message: MENSAGEM_SEM_VINCULO });
      } else {
        const { message: mensagem } = buildApiErrorMessage(
          err,
          'Não foi possível carregar seus pagamentos. Tente novamente.',
          { context: status === 401 ? 'session' : undefined }
        );
        setErro({ status, message: mensagem });
      }
      setPagamentos([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregarPagamentos();
  }, [carregarPagamentos]);

  const abrirDetalhes = async (pagamento) => {
    setModalAberto(true);
    setCarregandoDetalhe(true);
    setErroDetalhe(null);
    setDetalhe(null);
    try {
      const response = await professorPagamentosService.obter(pagamento.id);
      setDetalhe(response.data);
    } catch (err) {
      const { message: mensagem } = buildApiErrorMessage(
        err,
        'Não foi possível carregar o detalhamento deste pagamento.'
      );
      setErroDetalhe(mensagem);
    } finally {
      setCarregandoDetalhe(false);
    }
  };

  const fecharDetalhes = () => {
    setModalAberto(false);
    setDetalhe(null);
    setErroDetalhe(null);
  };

  const montarRecibo = (dados) => {
    const doc = new jsPDF();
    const mesFormatado = formatarCompetencia(dados.mes_referencia);
    const aulas = dados.aulas || [];
    const ajustes = dados.ajustes_retroativos || [];

    doc.setFontSize(14);
    doc.text('RECIBO DE PAGAMENTO', 105, 16, { align: 'center' });

    doc.setFontSize(10);
    const headerY = 25;
    doc.text('EMPRESA: Fisicore Assessoria de Dança e Esporte', 14, headerY);
    doc.text('CNPJ: 37.642.965/0001-98', 14, headerY + 6);
    doc.text(`PRESTADOR DE SERVIÇOS: ${user?.nome_completo || '-'}`, 14, headerY + 12);
    doc.text(`REFERÊNCIA: ${mesFormatado}`, 14, headerY + 18);
    doc.text(`STATUS: ${STATUS_LABEL[dados.status] || dados.status}`, 14, headerY + 24);
    doc.text(`TOTAL HORAS: ${Number(dados.total_horas || 0).toFixed(2)}`, 14, headerY + 30);

    autoTable(doc, {
      startY: headerY + 38,
      head: [['Data', 'Unidade', 'Modalidade', 'Turma', 'Início', 'Fim', 'Horas']],
      body: aulas.map((a) => [
        a.data_aula ? dayjs(a.data_aula).format('DD/MM/YYYY') : '-',
        a.unidade || '-',
        a.modalidade || '-',
        a.turma || '-',
        a.hora_inicio ? a.hora_inicio.slice(0, 5) : '-',
        a.hora_fim ? a.hora_fim.slice(0, 5) : '-',
        Number(a.duracao_horas || 0).toFixed(2),
      ]),
      theme: 'plain',
      styles: { fontSize: 8, textColor: [0, 0, 0], lineColor: [0, 0, 0], lineWidth: 0.1 },
      headStyles: { textColor: [0, 0, 0], lineColor: [0, 0, 0], lineWidth: 0.1 },
    });

    let currentY = doc.lastAutoTable?.finalY ? doc.lastAutoTable.finalY + 8 : headerY + 60;

    if (ajustes.length > 0) {
      doc.setFontSize(10);
      doc.text('AJUSTES RETROATIVOS (competências anteriores)', 14, currentY);
      currentY += 4;
      autoTable(doc, {
        startY: currentY,
        head: [['Descrição', 'Comp. Origem', 'Data Aula', 'Valor']],
        body: ajustes.map((item) => [
          item.descricao || '-',
          item.competencia_origem || '-',
          item.data_aula_referencia ? dayjs(item.data_aula_referencia).format('DD/MM/YYYY') : '-',
          formatarValor(item.valor),
        ]),
        theme: 'plain',
        styles: { fontSize: 8, textColor: [0, 0, 0], lineColor: [0, 0, 0], lineWidth: 0.1 },
        headStyles: { textColor: [0, 0, 0], lineColor: [0, 0, 0], lineWidth: 0.1 },
      });
      currentY = (doc.lastAutoTable?.finalY || currentY) + 8;
    }

    doc.setFontSize(10);
    doc.text('COMPOSIÇÃO DO PAGAMENTO:', 14, currentY);
    currentY += 6;
    doc.text(`Valor base: ${formatarValor(dados.valor_base)}`, 14, currentY);
    doc.text(`Ajustes retroativos: ${formatarValor(dados.valor_ajustes)}`, 14, currentY + 6);
    doc.text(`Valor final: ${formatarValor(dados.valor_final)}`, 14, currentY + 12);

    doc.text('Declaro que recebi a importância discriminada acima neste recibo:', 14, currentY + 24);
    doc.text(`Data: ${dados.data_pagamento ? dayjs(dados.data_pagamento).format('DD/MM/YYYY') : '-'}`, 14, currentY + 32);
    doc.text('Assinatura: __________________________________________', 14, currentY + 40);

    doc.save(`recibo_${dados.mes_referencia}.pdf`);
  };

  const baixarRecibo = async (pagamento) => {
    try {
      const response = await professorPagamentosService.obter(pagamento.id);
      montarRecibo(response.data);
    } catch (err) {
      message.error('Não foi possível gerar o recibo. Tente novamente.');
    }
  };

  const renderConteudo = () => {
    if (loading) {
      return (
        <div className="portal-professor__state" role="status" aria-live="polite">
          <Spin size="large" />
          <Text type="secondary">Carregando seus pagamentos...</Text>
        </div>
      );
    }

    if (erro) {
      return (
        <div className="portal-professor__state">
          <Alert
            type={erro.status === 403 ? 'warning' : 'error'}
            showIcon
            message={erro.status === 403 ? 'Acesso indisponível' : 'Não foi possível carregar'}
            description={erro.message}
            style={{ maxWidth: 480, textAlign: 'left' }}
          />
          {erro.status !== 403 && (
            <Button icon={<ReloadOutlined />} onClick={carregarPagamentos}>
              Tentar novamente
            </Button>
          )}
        </div>
      );
    }

    if (pagamentos.length === 0) {
      return (
        <div className="portal-professor__state">
          <Empty description="Nenhum pagamento encontrado." />
        </div>
      );
    }

    return (
      <div className="portal-professor__list">
        {pagamentos.map((pagamento) => (
          <PagamentoProfessorCard
            key={pagamento.id}
            pagamento={pagamento}
            onVerDetalhes={abrirDetalhes}
            onBaixarRecibo={baixarRecibo}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="portal-professor">
      <div className="portal-professor__header">
        <div>
          <Title level={3} className="portal-professor__title">
            Pagamentos
          </Title>
          <Text type="secondary" className="portal-professor__subtitle">
            Seu histórico de pagamentos
          </Text>
        </div>
      </div>

      {renderConteudo()}

      <Modal
        title={detalhe ? `Detalhes — ${formatarCompetencia(detalhe.mes_referencia)}` : 'Detalhes do pagamento'}
        open={modalAberto}
        onCancel={fecharDetalhes}
        footer={[
          <Button key="close" onClick={fecharDetalhes}>
            Fechar
          </Button>,
        ]}
        width={720}
      >
        {carregandoDetalhe && (
          <div className="portal-professor__state" role="status" aria-live="polite">
            <Spin />
            <Text type="secondary">Carregando detalhamento...</Text>
          </div>
        )}

        {!carregandoDetalhe && erroDetalhe && (
          <Alert type="error" showIcon message="Não foi possível carregar" description={erroDetalhe} />
        )}

        {!carregandoDetalhe && detalhe && (
          <>
            <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Situação">
                {STATUS_LABEL[detalhe.status] || detalhe.status}
              </Descriptions.Item>
              <Descriptions.Item label="Total de horas">
                {Number(detalhe.total_horas || 0).toFixed(2)}
              </Descriptions.Item>
              <Descriptions.Item label="Valor base">{formatarValor(detalhe.valor_base)}</Descriptions.Item>
              <Descriptions.Item label="Ajustes">{formatarValor(detalhe.valor_ajustes)}</Descriptions.Item>
              <Descriptions.Item label="Valor final" span={2}>
                <Text strong>{formatarValor(detalhe.valor_final)}</Text>
              </Descriptions.Item>
              {detalhe.data_pagamento && (
                <Descriptions.Item label="Data do pagamento" span={2}>
                  {dayjs(detalhe.data_pagamento).format('DD/MM/YYYY')}
                </Descriptions.Item>
              )}
            </Descriptions>

            <Text strong>Aulas incluídas</Text>
            <Table
              style={{ marginTop: 8, marginBottom: 16 }}
              size="small"
              pagination={false}
              rowKey={(_, index) => index}
              dataSource={detalhe.aulas || []}
              locale={{ emptyText: 'Nenhuma aula vinculada.' }}
              columns={[
                { title: 'Data', dataIndex: 'data_aula', render: (v) => dayjs(v).format('DD/MM/YYYY') },
                { title: 'Turma', dataIndex: 'turma' },
                { title: 'Modalidade', dataIndex: 'modalidade' },
                { title: 'Unidade', dataIndex: 'unidade' },
                {
                  title: 'Horas',
                  dataIndex: 'duracao_horas',
                  render: (v) => (v != null ? Number(v).toFixed(2) : '-'),
                },
              ]}
            />

            {(detalhe.ajustes_retroativos || []).length > 0 && (
              <>
                <Text strong>Ajustes retroativos</Text>
                <Table
                  style={{ marginTop: 8 }}
                  size="small"
                  pagination={false}
                  rowKey={(_, index) => index}
                  dataSource={detalhe.ajustes_retroativos}
                  columns={[
                    { title: 'Descrição', dataIndex: 'descricao' },
                    { title: 'Competência origem', dataIndex: 'competencia_origem', render: (v) => v || '-' },
                    { title: 'Valor', dataIndex: 'valor', render: (v) => formatarValor(v) },
                  ]}
                />
              </>
            )}
          </>
        )}
      </Modal>
    </div>
  );
};

export default PortalProfessorPagamentos;
